﻿import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from "react-router-dom";
import { ColumnHeader } from '../models/column-header';
import { ColumnEntry } from '../models/column-entry';
import { ColumnHeaderFilter } from '../models/column-header-filter';
import { Vendor } from '../models/vendor';
import { Document } from '../models/document';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { CrudFunctions } from '../utilities/crud-functions';
import { Asset } from '../models/asset';
import { BenchSheetTechnologyType, DocumentType } from '../models/enums';
import DocumentLoader from './DocumentLoader';
import { DocumentationConstants } from '../store/documentation-constants';
import DmaFooter from './DmaFooter';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import RowHistoryPopup, { RowHistoryPopupRef } from './Auditing/RowHistoryPopup';

import DataGrid, {
    Column,
    FilterRow,
    GroupPanel,
    Selection,
    HeaderFilter,
    Pager,
    Paging,
    Editing,
    Popup,
    Form,
    Lookup,
    Button as ColumnButton,
    SearchPanel,
    Toolbar,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { TextBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { Item, Label } from 'devextreme-react/form';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export default function ColumnsForm() {
    const [vendors, setVendors] =
        useState<Vendor[]>();

    const [gcColumnHeaders, setGcColumnHeaders] =
        useState<ColumnHeader[]>();

    const [selectedGcColumnHeader, setSelectedGcColumnHeader] =
        useState<ColumnHeader>();

    const [hplcColumnHeaders, setHplcColumnHeaders] =
        useState<ColumnHeader[]>();

    const [selectedHplcColumnHeader, setSelectedHplcColumnHeader] =
        useState<ColumnHeader>();

    const [gcColumnEntries, setGcColumnEntries] =
        useState<ColumnEntry[]>();

    const [hplcColumnEntries, setHplcColumnEntries] =
        useState<ColumnEntry[]>();

    const [currentGcCertificate, setCurrentGcCertificate] =
        useState<Document[]>();

    const [currentHplcCertificate, setCurrentHplcCertificate] =
        useState<Document[]>();

    const [gcPartNumberFilter, setGcPartNumberFilter] =
        useState<string>();

    const [gcLotNumberFilter, setGcLotNumberFilter] =
        useState<string>();

    const [gcFilterOn, setGcFilterOn] =
        useState<boolean>();

    const [hplcPartNumberFilter, setHplcPartNumberFilter] =
        useState<string>();

    const [hplcLotNumberFilter, setHplcLotNumberFilter] =
        useState<string>();

    const [hplcFilterOn, setHplcFilterOn] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [hplcCerticatesDialogVisible, setHplcCerticatesDialogVisible] =
        useState<boolean>();

    const [gcCerticatesDialogVisible, setGcCerticatesDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        initialUiSetup();
        getAllData();
    }, []);

    ++key;

    const rowHistoryPopupRef = useRef<RowHistoryPopupRef>(null);

    return (
        <div className="pw-content">
            <div className="lq-title-bar pw-form-header">
                <div className="lq-main-title">Columns</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <div>
                        <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back" className="title-bar-button"
                            height={'2.6em'}
                            onClick={goBack} />
                    </div>
                    <div>
                        <NavLink to={`/documentation/${DocumentationConstants.ColumnsForm}`}
                            className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                            <Button className="title-bar-button pw-orange"
                                text="" icon="help" type="normal"
                                hint="help and documentation" />
                        </NavLink>
                    </div>
                </div>
            </div>

            <div>
                <div id="tab-buttons-panel" className="tab-buttons-container">
                    <div id="gcColumnsPanelButton" className="tab-button" onClick={onPanelButtonClick}>GC Columns</div>
                    <div id="hplcColumnsPanelButton" className="tab-button" onClick={onPanelButtonClick}>HPLC Columns</div>
                </div>
            </div>

            <div id="gcColumnsPanel">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-15 margin-top-12">
                        <div className="pw-form-unit-label">Filter by Column Entry Values:</div>
                    </div>

                    <div className="pw-form-unit pw-comp-10 pw-margin-bottom-05">
                        <div className="pw-form-unit-label pw-comp-10">Part #</div>
                        <TextBox id="partNumberFilterTextBox" placeholder="na"
                            onValueChanged={onGcPartNumberFilterChanged}
                            value={gcPartNumberFilter ? gcPartNumberFilter : undefined} />
                    </div>
                    <div className="pw-form-unit pw-comp-10 pw-margin-bottom-05">
                        <div className="pw-form-unit-label">Lot #</div>
                        <TextBox id="lotNumberFilterTextBox"
                            placeholder="na"
                            onValueChanged={onGcLotNumberFilterChanged}
                            value={gcLotNumberFilter ? gcLotNumberFilter : undefined} />
                    </div>

                    <div className="form-unit pw-margin-top-08">
                        <Button className="title-bar-button"
                            text="Filter" icon="search" type="success"
                            onClick={onFilterGcColumnsByEntryValuesClicked} />

                        {gcFilterOn &&
                            <Button className="title-bar-button"
                                text="Remove Filter" icon="clear" type="success"
                                onClick={onRemoveGcFilterClicked} />
                        }
                    </div>
                </div>

                <div className="pw-margin-bottom-05">
                    <DataGrid
                        height="28em"
                        width="100%"
                        dataSource={gcColumnHeaders ? gcColumnHeaders : []}
                        onSelectionChanged={onGcColumnHeadersSelectionChanged}
                        onRowValidating={onGcColumnHeaderRowValidating}
                        allowColumnResizing={true}
                        onInitNewRow={onInitNewGcColumnRow}
                        wordWrapEnabled={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true} >

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={false} />
                        <SearchPanel visible={true} />
                        <Paging defaultPageSize={30} />
                        <Pager visible={true} displayMode="compact"
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 100, 'all']}
                            showInfo={true} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton hint="certificate" icon="bookmark"
                                onClick={showGcCertificatesDialog}
                                render={renderDocumentsColumnButton} />
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={showColumnHeadersAuditDialog} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onGcColumnHeaderInformationButtonClicked} />
                        </Column>

                        <Column dataField='columnId' caption="Column ID" width='7rem' cellRender={datagridCellRender} />

                        <Column dataField='vendorId' caption="Vendor" width='10rem'>
                            <Lookup dataSource={vendors ? vendors : []}
                                displayExpr='name'
                                valueExpr='vendorId' />
                        </Column>

                        <Column dataField='manufacturerId' caption="Manufacturer" width='10rem'>
                            <Lookup dataSource={vendors ? vendors : []}
                                displayExpr='name'
                                valueExpr='vendorId' />
                        </Column>

                        <Column dataField='description' width='16rem' />
                        <Column dataField='dimensions' width='20rem' />
                        <Column dataField='dateObtained' dataType="date" width='6rem' />
                        <Column dataField='dateRetired' dataType="date" width='6rem' />

                        <Column dataField='vendorPartNumber' caption="Vendor Part#" width='9rem' visible={false} />
                        <Column dataField='vendorSerialNumber' caption="Vendor Serial#" width='9rem' visible={false} />
                        <Column dataField='length' dataType="number" width='5rem' visible={false} />
                        <Column dataField='lengthUnitsName' dataType="number" width='4rem' caption="UOM" visible={false} />
                        <Column dataField='diameter' dataType="number" width='6rem' visible={false} />
                        <Column dataField='diameterUnitsName' dataType="number" width='4rem' caption="UOM" visible={false} />
                        <Column dataField='filmThickness' dataType="number" width='6rem' visible={false} />
                        <Column dataField='filmThicknessUnitsName' dataType="number" width='4rem' caption="UOM" visible={false} />
                        <Column dataField='uspPhase' width='8rem' visible={false} />
                        <Column dataField='maxTemperature' dataType="number" caption="Max Temp" width='6rem' visible={false} />
                        <Column dataField='notes' width="20rem" visible={false} />

                        <Column />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">GC Columns</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem location="after" name="addRowButton" />
                        </Toolbar>

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={false} >

                            <Popup title="GC Column" showTitle={true} width="80vw" height="80vh" />
                            <Form colCount={3}>
                                <Item dataField="columnId" disabled={true} />
                                <Item dataField="dateObtained" />
                                <Item dataField="dateRetired" />

                                <Item dataField="description" colSpan={2} />
                                <Item dataField="vendorId">
                                    <Label text="Vendor" />
                                </Item>
                                <Item dataField="manufacturerId">
                                    <Label text="Manufacturer" />
                                </Item>
                                <Item dataField="vendorPartNumber" >
                                    <Label text="Vendor Part #" />
                                </Item>
                                <Item dataField="vendorSerialNumber">
                                    <Label text="Vendor Serial #" />
                                </Item>
                                <Item dataField="length">
                                    <Label text="Length (m)" />
                                </Item>
                                <Item dataField="diameter">
                                    <Label text="Diameter (mm)" />
                                </Item>
                                <Item dataField="filmThickness">
                                    <Label text="Film Thickness (μm)" />
                                </Item>
                                <Item dataField="maxTemperature">
                                    <Label text="Max Temperature (°C)" />
                                </Item>
                                <Item dataField="uspPhase">
                                    <Label text="USP Phase" />
                                </Item>
                                <Item />
                                <Item dataField="notes" colSpan={3} editorType="dxTextArea" editorOptions={{ height: "10rem" }} />
                            </Form>
                        </Editing>
                    </DataGrid>
                </div>

                <div className="pw-margin-bottom-05">
                    <DataGrid
                        height="28em"
                        width="100%"
                        dataSource={gcColumnEntries ? gcColumnEntries : []}
                        allowColumnResizing={true}
                        wordWrapEnabled={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={false} />
                        <SearchPanel visible={true} />
                        {/* <Scrolling mode={'virtual'} /> */}

                        <Column type="buttons" width="6rem">
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={showColumnEntriesAuditDialog} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onGcColumnEntryInformationButtonClicked} />
                        </Column>

                        <Column dataField='entryNumber' caption="Entry ID" width='6rem' />
                        <Column dataField='createTimestamp' caption="Date" dataType="date" format="MM/dd/yyyy" width='8rem' />
                        <Column dataField='analyst' caption="Analyst" width='8rem' />
                        <Column dataField='partNumber' caption="Part #" width='8rem' />
                        <Column dataField='batchName' width='8rem' />
                        <Column dataField='formattedAssetNumber' caption="Asset#" width='6rem' />
                        <Column dataField='assetName' width='8rem' />
                        <Column dataField='runLowTemperature' caption="Low Temp" width='7rem' />
                        <Column dataField='runHighTemperature' caption="High Temp" width='7rem' />
                        <Column dataField='runNumberOfInjections' caption="# of Injections" width='7rem' />
                        <Column dataField='columnLinearVelocity' caption="Linear Vel." width='8rem' visible={false} />
                        <Column dataField='columnFlowRate' caption="FlowRate" width='8rem' visible={false} />

                        <Column dataField='runSolvent' caption="Solvent" width='10rem' />
                        {/* <Column dataField={'runNotes'} caption="Notes" width={'20rem'} /> */}
                        <Column />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">GC Column Entries</div>
                                </div>
                            </ToolBarItem>

                            {selectedGcColumnHeader &&
                                <ToolBarItem location="after" name="addRowButton" />
                            }
                        </Toolbar>

                    </DataGrid>
                </div>

                <DmaFooter />
            </div>

            <div id="hplcColumnsPanel" className="pw-margin-bottom-05">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-15 margin-top-12">
                        <div className="pw-form-unit-label">Filter by Column Entry Values:</div>
                    </div>

                    <div className="pw-form-unit pw-comp-10 pw-margin-bottom-05">
                        <div className="pw-form-unit-label pw-comp-10">Part #</div>
                        <TextBox id="hplcPpartNumberFilterTextBox" placeholder="na"
                            onValueChanged={onHplcPartNumberFilterChanged}
                            value={hplcPartNumberFilter ? hplcPartNumberFilter : undefined} />
                    </div>
                    <div className="pw-form-unit pw-comp-10 pw-margin-bottom-05">
                        <div className="pw-form-unit-label">Lot #</div>
                        <TextBox id="hplcLotNumberFilterTextBox"
                            placeholder="na"
                            onValueChanged={onHplcLotNumberFilterChanged}
                            value={hplcLotNumberFilter ? hplcLotNumberFilter : undefined} />
                    </div>

                    <div className="form-unit pw-margin-top-08">
                        <Button className="title-bar-button"
                            text="Filter" icon="search" type="success"
                            onClick={onFilterHplcColumnsByEntryValuesClicked} />

                        {hplcFilterOn &&
                            <Button className="title-bar-button"
                                text="Remove Filter" icon="clear" type="success"
                                onClick={onRemoveHplcFilterClicked} />
                        }
                    </div>
                </div>

                <div className="pw-margin-bottom-05">
                    <DataGrid height="28em" width="100%"
                        dataSource={hplcColumnHeaders ? hplcColumnHeaders : []}
                        onSelectionChanged={onHplcColumnHeadersSelectionChanged}
                        onRowValidating={onHplcColumnHeaderRowValidating}
                        onInitNewRow={onInitNewHplcColumnRow}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={false} />
                        <SearchPanel visible={true} />
                        <Paging defaultPageSize={30} />
                        <Pager visible={true} displayMode="compact"
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 100, 'all']}
                            showInfo={true} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton hint="certificate" icon="bookmark"
                                onClick={showHplcCertificatesDialog}
                                render={renderDocumentsColumnButton} />
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={showColumnHeadersAuditDialog} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onHplcColumnHeaderInformationButtonClicked} />
                        </Column>

                        <Column dataField='columnId' width='5rem' cellRender={datagridCellRender} />

                        <Column dataField='vendorId' caption="Vendor" width='10rem'>
                            <Lookup dataSource={vendors ? vendors : []}
                                displayExpr='name'
                                valueExpr='vendorId' />
                        </Column>

                        <Column dataField='manufacturerId' caption="Manufacturer" width='10rem'>
                            <Lookup dataSource={vendors ? vendors : []}
                                displayExpr='name'
                                valueExpr='vendorId' />
                        </Column>

                        <Column dataField='description' width='12rem' />
                        <Column dataField='model' width='8rem' />
                        <Column dataField='dimensions' width='20rem' />
                        <Column dataField='vendorPartNumber' width='15em' visible={false} />
                        <Column dataField='vendorSerialNumber' width='15em' visible={false} />
                        <Column dataField='length' dataType="number" width='8em' visible={false} />
                        <Column dataField='lengthUnitsName' dataType="number" width='8em' caption="UOM" visible={false} />
                        <Column dataField='diameter' dataType="number" width='8em' visible={false} />
                        <Column dataField='diameterUnitsName' dataType="number" width='8em' caption="UOM" visible={false} />
                        <Column dataField='particleSize' dataType="number" width='12em' visible={false} />
                        <Column dataField='particleSizeUnitsName' width='8em' caption="UOM" visible={false} />
                        <Column dataField='uspPhase' caption="USP Phase" width='15em' visible={false} />
                        <Column dataField='phRange' caption="pH Range" width='8rem' />
                        <Column dataField='ionPair' dataType="boolean" width='6rem' />
                        <Column dataField='normalPhase' dataType="boolean" width='7rem' />
                        <Column dataField='dateObtained' dataType="date" width='6rem' />
                        <Column dataField='dateRetired' dataType="date" width='6rem' />
                        <Column dataField='notes' width="30em" visible={false} />

                        <Column />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">HPLC Columns</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem location="after" name="addRowButton" />
                        </Toolbar>

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={false} >

                            <Popup title="HPLC Column" showTitle={true} width="80vw" height="80vh" />
                            <Form colCount={3}>
                                <Item dataField="columnId" disabled={true} />
                                <Item dataField="dateObtained" />
                                <Item dataField="dateRetired" />
                                <Item dataField="description" />
                                <Item dataField="model" />
                                <Item dataField="manufacturerId">
                                    <Label text="Manufacturer" />
                                </Item>
                                <Item dataField="vendorId">
                                    <Label text="Vendor" />
                                </Item>
                                <Item dataField="vendorPartNumber" >
                                    <Label text="Vendor Part #" />
                                </Item>
                                <Item dataField="vendorSerialNumber">
                                    <Label text="Vendor Serial #" />
                                </Item>
                                <Item dataField="length">
                                    <Label text="Length (cm)" />
                                </Item>
                                <Item dataField="diameter">
                                    <Label text="Diameter (mm)" />
                                </Item>
                                <Item dataField="particleSize">
                                    <Label text="Particle Size (μm)" />
                                </Item>
                                <Item dataField="uspPhase">
                                    <Label text="USP Phase" />
                                </Item>
                                <Item dataField="phRange">
                                    <Label text="pH Range" />
                                </Item>
                                <Item></Item>
                                <Item dataField="ionPair" editorType="dxCheckBox" />
                                <Item dataField="normalPhase" editorType="dxCheckBox" colSpan={2} />
                                <Item dataField="notes" colSpan={3} editorType="dxTextArea" editorOptions={{ height: "10rem" }} />

                            </Form>
                        </Editing>
                    </DataGrid>
                </div>

                <div className="pw-margin-bottom-05">
                    <DataGrid
                        height="28em"
                        width="100%"
                        dataSource={hplcColumnEntries ? hplcColumnEntries : []}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        wordWrapEnabled={true}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true} >

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={false} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width="4rem">
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={showColumnEntriesAuditDialog} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onHplcColumnEntryInformationButtonClicked} />
                        </Column>
                        <Column dataField='entryNumber' caption="Entry ID" width='6rem' />
                        <Column dataField='createTimestamp' caption="Date" dataType="date" format="MM/dd/yyyy" width='8rem' />
                        <Column dataField='analyst' caption="Analyst" width='8rem' />
                        <Column dataField='partNumber' caption="Part #" width='8rem' />
                        <Column dataField='batchName' width='8rem' />
                        <Column dataField='formattedAssetNumber' caption="Asset#" width='6rem' />
                        <Column dataField='assetName' width='8rem' />
                        <Column dataField='compartmentTemperature' width='8rem' />
                        <Column dataField='runMobilePhase' />
                        {/* <Column dataField={'runNotes'} caption="Notes" width={'20em'} />*/}

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">HPLC Column Entries</div>
                                </div>
                            </ToolBarItem>
                            {selectedHplcColumnHeader &&
                                <ToolBarItem location="after" name="addRowButton" />
                            }
                        </Toolbar>

                    </DataGrid>
                </div>

                <DmaFooter />
            </div>

            <RowHistoryPopup ref={rowHistoryPopupRef} />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {gcCerticatesDialogVisible &&
                <DialogPopup visible={gcCerticatesDialogVisible}
                    onHiding={hideGcCertificatesDialog}
                    title="GC Column Certificate"
                    width={'35rem'}
                    height={'25rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <DocumentLoader parentId={selectedGcColumnHeader &&
                        selectedGcColumnHeader.columnHeaderId ?
                        selectedGcColumnHeader.columnHeaderId : undefined}
                        documentType={DocumentType.ColumnCertificate}
                        readOnly={false} canDelete={true} />
                </DialogPopup>
            }

            {hplcCerticatesDialogVisible &&
                <DialogPopup visible={hplcCerticatesDialogVisible}
                    onHiding={hideHplcCertificatesDialog}
                    title="HPLC Column Certificate"
                    width={'35rem'}
                    height={'25rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <DocumentLoader
                        readOnly={false} canDelete={true}
                        parentId={selectedHplcColumnHeader &&
                            selectedHplcColumnHeader.columnHeaderId ?
                            selectedHplcColumnHeader.columnHeaderId : undefined}
                        documentType={DocumentType.ColumnCertificate} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function onGcPartNumberFilterChanged(e: any) {
        setGcPartNumberFilter(e.value);
    }

    function onGcLotNumberFilterChanged(e: any) {
        setGcLotNumberFilter(e.value);
    }

    function onHplcPartNumberFilterChanged(e: any) {
        setHplcPartNumberFilter(e.value);
    }

    function onHplcLotNumberFilterChanged(e: any) {
        setHplcLotNumberFilter(e.value);
    }

    function onGcColumnHeadersSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedColumnHeader = e.selectedRowsData[0];

            setSelectedGcColumnHeader(selectedColumnHeader);
            getGcColumnEntries(selectedColumnHeader.columnHeaderId)
        }
    }

    function onHplcColumnHeadersSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedColumnHeader = e.selectedRowsData[0];

            setSelectedHplcColumnHeader(selectedColumnHeader);
            getHplcColumnEntries(selectedColumnHeader.columnHeaderId);
        }
    }

    async function onInitNewGcColumnRow(e: any) {
        e.promise = getNextGcColumnId()
            .then((data) => {
                if (data && data.value) {
                    let newGcColumn: ColumnHeader = {
                        columnId: data.value.toString()
                    };

                    e.data = newGcColumn;
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Next Column ID");
            });
    }

    async function onInitNewHplcColumnRow(e: any) {
        e.promise = getNextHplcColumnId()
            .then((data) => {
                if (data && data.value) {
                    let newHplcColumn: ColumnHeader = {
                        columnId: data.value.toString()
                    };

                    newHplcColumn.ionPair = false;
                    newHplcColumn.normalPhase = false;

                    e.data = newHplcColumn;
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Creating New HPLC Column Row");
            });
    }

    async function onFilterGcColumnsByEntryValuesClicked() {
        let filter: ColumnHeaderFilter = {
            technologyId: BenchSheetTechnologyType.GasChromatography,
            partNumber: gcPartNumberFilter,
            lotNumber: gcLotNumberFilter
        };

        await getFilteredGcColumnHeaders(filter)
            .then(() => {
                setGcFilterOn(true);
            })
    }

    async function onFilterHplcColumnsByEntryValuesClicked() {
        let filter: ColumnHeaderFilter = {
            technologyId: BenchSheetTechnologyType.HighPerformanceLiquidChromatography,
            partNumber: hplcPartNumberFilter,
            lotNumber: hplcLotNumberFilter
        };

        await getFilteredHplcColumnHeaders(filter)
            .then(() => {
                setHplcFilterOn(true);
            })
    }

    async function onRemoveGcFilterClicked() {
        await getGcColumnHeaders()
            .then(() => {
                setGcFilterOn(false);
                setGcColumnEntries([]);
            })
    }

    async function onRemoveHplcFilterClicked() {
        await getHplcColumnHeaders()
            .then(() => {
                setHplcFilterOn(false);
                setHplcColumnEntries([]);
            })
    }

    // validating

    async function onGcColumnHeaderRowValidating(e: any) {
        if (!dataLoaded)
            return;

        if (e.oldData) {
            let gcColumnHeader = e.oldData as ColumnHeader;

            if (gcColumnHeader) {
                let updatedGcColumnHeader = { ...gcColumnHeader, ...e.newData };

                updateColumnHeader(updatedGcColumnHeader)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating GC Column Header");
                    });
            }
        }
        else {
            if (e.newData) {
                let gcColumnHeader = e.newData as ColumnHeader;

                if (gcColumnHeader) {
                    gcColumnHeader.technologyId = BenchSheetTechnologyType.GasChromatography;

                    await insertGcColumnHeader(gcColumnHeader)
                        .then(() => {
                            getGcColumnHeaders();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting New GC Column Header");
                        });
                }
            }
        }
    }

    async function onHplcColumnHeaderRowValidating(e: any) {
        if (!dataLoaded)
            return;

        if (e.oldData) {
            let hplcColumnHeader = e.oldData as ColumnHeader;

            if (hplcColumnHeader) {
                let updatedHplcColumnHeader = { ...hplcColumnHeader, ...e.newData };

                updateColumnHeader(updatedHplcColumnHeader)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating HPLC Column Header");
                    });
            }
        }
        else {
            if (e.newData) {
                let hplcColumnHeader = e.newData as ColumnHeader;

                if (hplcColumnHeader) {
                    hplcColumnHeader.technologyId = BenchSheetTechnologyType.HighPerformanceLiquidChromatography;

                    await insertHplcColumnHeader(hplcColumnHeader)
                        .then(() => {
                            getHplcColumnHeaders();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting HPLC Column Header");
                        });
                }
            }
        }
    }


    function onGcColumnHeaderInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.columnHeaderId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            showItemBasicInformationDialog();
        }
    }

    function onGcColumnEntryInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.columnEntryId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            showItemBasicInformationDialog();
        }
    }

    function onHplcColumnHeaderInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.columnHeaderId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            showItemBasicInformationDialog();
        }
    }

    function onHplcColumnEntryInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.columnEntryId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            showItemBasicInformationDialog();
        }
    }

    // get functions

    async function getGcColumnHeaders() {
        await CrudFunctions.getData(`common/columnheaders/tid/${BenchSheetTechnologyType.GasChromatography}`)
            .then(data => {
                //console.log(data);

                setGcColumnHeaders(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving GC Column Headers");
            });
    }

    async function getFilteredGcColumnHeaders(filter: ColumnHeaderFilter) {
        await CrudFunctions.postData("common/columnheaderssearch/pl", filter)
            .then(data => {
                setGcColumnHeaders(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving GC Column Headers");
            });
    }

    async function getGcColumnEntries(columnHeaderId: string) {
        await CrudFunctions.getData(`common/columnentries/${columnHeaderId}`)
            .then(data => {
                setGcColumnEntries(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving GC Column Entries");
            });
    }

    async function getHplcColumnHeaders() {
        await CrudFunctions.getData(`common/columnheaders/tid/${BenchSheetTechnologyType.HighPerformanceLiquidChromatography}`)
            .then(data => {
                setHplcColumnHeaders(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving HPLC Column Headers");
            });
    }

    async function getFilteredHplcColumnHeaders(filter: ColumnHeaderFilter) {
        await CrudFunctions.postData("common/columnheaderssearch/pl", filter)
            .then(data => {
                setHplcColumnHeaders(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving HPLC Column Headers");
            });
    }

    async function getHplcColumnEntries(columnHeaderId: string) {
        await CrudFunctions.getData(`common/columnentries/${columnHeaderId}`)
            .then(data => {

                console.log("ENTRIES");
                console.log(data);

                setHplcColumnEntries(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving HPLC Column Entries");
            });
    }

    async function getVendors() {
        await CrudFunctions.getData("common/vendors")
            .then(data => {
                setVendors(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Vendors");
            });
    }

    //todo: change this to select also by DocumentType:
    //            documentsbyparentid/{parentId}/{documentType}

    async function getColumnCertificate(columnHeaderId: string) {
        return await CrudFunctions.getData(`media/documentsbyparentid/${columnHeaderId}/${DocumentType.ColumnCertificate}`);
    }

    async function getNextGcColumnId() {
        return await await CrudFunctions.getData("common/nextgccolumnid");
    }

    async function getNextHplcColumnId() {
        return await CrudFunctions.getData("common/nexthplccolumnid");
    }

    //POST functions

    async function insertGcColumnHeader(gcColumnHeader: ColumnHeader) {
        return CrudFunctions.postData("common/gccolumnheader", gcColumnHeader);
    }

    async function insertColumnEntry(gcColumnEntry: ColumnEntry) {
        return CrudFunctions.postData("common/columnentry", gcColumnEntry);
    }

    async function insertHplcColumnHeader(hplcColumnHeader: ColumnHeader) {
        return CrudFunctions.postData("common/hplccolumnheader", hplcColumnHeader);
    }

    async function insertColumnCertificate(document: Document) {
        return await CrudFunctions.postData("media/document", document);
    }

    //PUT functions

    async function updateColumnHeader(gcColumnHeader: ColumnHeader) {
        return CrudFunctions.putData("common/columnheader", gcColumnHeader);
    }

    //render

    /*
    //sort of worked but not completely - left for now
    // see https://codesandbox.io/s/form-item-template-in-react-5qem9?file=/App.js:2541-2596
    renderInstrumentsSelectBox({ component, dataField, editorOptions }) {
       
        return (
            <SelectBox
                items={this.state.instruments ? this.state.instruments : []}
                itemRender={DataTemplates.GcAssetItemRender}
                onValueChanged={args => {
    
                    if(args.value && args.value.assetId)
                        component.updateData(dataField, args.value.assetId);
                }}

                value={this.state.selectedGcColumnEntry && this.state.selectedGcColumnEntry.assetId ?
                    this.state.selectedGcColumnEntry.assetId : undefined}
                defaultValue={component.option("formData")[dataField]}
             />
        );
    };
    */

    function renderDocumentsColumnButton(e: any) {
        if (e.data && e.data.documentCount > 0)
            return <><i className="dx-icon-bookmark pw-green cursor-pointer"></i><span className="pw-green pw-normal">{e.data.documentCount}</span></>;
        return <i className="dx-icon-bookmark cursor-pointer"></i>;
    }


    //show/hide

    function showItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(true);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showColumnHeadersAuditDialog(e: any) {
        if (e.row && e.row.data) {
            rowHistoryPopupRef.current?.open('ColumnHeaders', e.row!.data.columnHeaderId);
        }
    }

    function showColumnEntriesAuditDialog(e: any) {
        if (e.row && e.row.data) {
            rowHistoryPopupRef.current?.open('ColumnEntries', e.row!.data.columnEntryId);
        }
    }

    async function showGcCertificatesDialog(e: any) {
        //console.log("showGcCertificatesDialog");
        // console.log(e);
        // console.log(selectedGcColumnHeader);

        if (e.row && e.row.data) {
            const columnHeaderId = e.row.data.columnHeaderId;

            if (columnHeaderId) {
                await getColumnCertificate(columnHeaderId)
                    .then(data => {
                        setCurrentGcCertificate(data);
                        setGcCerticatesDialogVisible(true);
                        setSelectedGcColumnHeader(e.row.data);

                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Retrieving GC Column Certificate");
                    });
            }
        }
    }

    async function showHplcCertificatesDialog(e: any) {
        console.log("showHplcCertificatesDialog");
        console.log(e);

        if (e.row && e.row.data) {
            const columnHeaderId = e.row.data.columnHeaderId;

            await getColumnCertificate(columnHeaderId)
                .then(data => {
                    /*
                    this.setState({
                        currentHplcCertificate: data,
                        hplcCerticatesDialogVisible: true,
                        selectedHplcColumnHeader: e.row.data
                    });
                    */

                    setCurrentHplcCertificate(data);
                    setHplcCerticatesDialogVisible(true);
                    setSelectedHplcColumnHeader(e.row.data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving HPLC Column Certificate");
                });
        }
    }

    function hideGcCertificatesDialog() {
        setGcCerticatesDialogVisible(false);
    }

    function hideHplcCertificatesDialog() {
        setHplcCerticatesDialogVisible(false);
    }

    //other
    function onPanelButtonClick(e: any) {
        resetAllButtons();

        const button = e.target as HTMLDivElement;

        if (button) {
            button.className = "tab-button-active";
        }

        hideAllComponents();

        const id: string = e.target.id;
        const tab: string = id.replace("Button", "");

        const panel = document.getElementById(tab) as HTMLDivElement;
        panel.style.display = "";
    }

    function resetAllButtons() {
        const gcColumnsPanelButton = document.getElementById("gcColumnsPanelButton") as HTMLDivElement;
        gcColumnsPanelButton.className = "tab-button-inactive";

        const hplcColumnsPanelButton = document.getElementById("hplcColumnsPanelButton") as HTMLDivElement;
        hplcColumnsPanelButton.className = "tab-button-inactive";
    }

    function hideAllComponents(onload: boolean = false) {
        if (!onload) {
            const gcColumnsPanel = document.getElementById("gcColumnsPanel") as HTMLDivElement;
            gcColumnsPanel.style.display = "none";
        }

        const hplcColumnsPanel = document.getElementById("hplcColumnsPanel") as HTMLDivElement;
        hplcColumnsPanel.style.display = "none";
    }

    //render

    function datagridCellRender(e: any) {
        if (e.data) {
            const css = e.data.dateRetired ? "validation-fail" : "validation-pass";
            return (<div className={css}>{e.displayValue}</div>);
        }
        else
            return (<div>{e.displayValue}</div>);
    }

    //navigation

    function goBack() {
        history.back();
    }

    //other

    function initialUiSetup() {
        hideAllComponents(true);

        const gcColumnsPanelButton = document.getElementById("gcColumnsPanelButton") as HTMLButtonElement;
        gcColumnsPanelButton.click();
    }

    async function getAllData() {
        await Promise.all([
            getVendors(),
            getGcColumnHeaders(),
            getHplcColumnHeaders()
        ])
            .then(() => setDataLoaded(true));
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}
